/* eslint-disable max-lines */
import moment from 'moment';
import { Button, Card, CardBody, Col, Row, Table, Spinner } from 'reactstrap';
import EmploymentDocumentsService from '../../../../services/EmploymentDocuments';
import BankingAccountsService from '../../../../services/BankingAccounts';
import HireApplication from '../../../../services/HireApplication';
import HireDocuments from '../../../../services/HireDocuments';
import HireAgreementsService from '../../../../services/HireAgreements';
import { useEffect, useRef, useState } from 'react';
import confirmAlert from '../../../../helpers/confirmAlert';
import W4DocumentViewModal from './W4DocumentViewModal';
import I9DocumentViewModal from './I9DocumentViewModal';
import I9SpanishDocumentViewModal from './I9SpanishDocumentViewModal';
import BankingAccountModal from '../../NewHire/BankingAccountModal';
import PreEmploymentFormModal from './PreEmploymentFormModal';
import OfferLetterView from './OfferLetterView';
import isAdmin from '../../../../helpers/isAdmin';
import { toast } from 'react-toastify';
import UploadDocumentsForm from '../../NewHire/UploadDocumentsForm';
import IS54FormModal from './IS54FormModal';
import IT2104PreviewModal from './IT2104PreviewModal';
import NC4PreviewModal from './NC4PreviewModal';
import isHRUser from '../../../../helpers/isHRUser';
import isAuthorized from '../../../../helpers/isAuthorized';
import VerifyEmploymentDocumentsModal, { VerifiableDocument } from './VerifyEmploymentDocumentsModal';

type Props = {
  loggedUser: any;
  currentUser: any;
  token: any;
  removeAlert: (e: any, id: string) => Promise<void>;
  denyAlert: (e: any, id: string) => Promise<void>;
  approveAlert: (e: any, id: string) => Promise<void>;
  handleDocument: (e?: any) => void;
  onDocumentsUpdated?: (refreshFn: () => Promise<void>) => void;
};

const NewHireDocumentsView = ({
  loggedUser,
  currentUser,
  token,
  removeAlert,
  denyAlert,
  approveAlert,
  handleDocument,
  onDocumentsUpdated,
}: Props) => {
  const [uploads, setUploads] = useState<any[]>([]);
  const [verifyingDocument, setVerifyingDocument] = useState<VerifiableDocument | null>(null);
  const canVerifyDocuments = isAuthorized({ moduleName: 'HireDocuments', functionName: 'Approve' }, loggedUser);
  const [banks, setBanks] = useState<any[]>([]);
  const [showBankingModal, setShowBankingModal] = useState(false);
  const [currentBankingAccount, setCurrentBankingAccount] = useState<any>({});
  const [currentW4Document, setCurrentW4Document] = useState<any>();
  const [showW4Document, setShowW4Document] = useState<boolean>(false);
  const [currentIS54Document, setCurrentIS54Document] = useState<any>();
  const [currentIT2104Document, setCurrentIT2104Document] = useState<any>();
  const [currentNC4Document, setCurrentNC4Document] = useState<any>();
  const [showIS54Document, setShowIS54Document] = useState<boolean>(false);
  const [showIT2104Document, setShowIT2104Document] = useState<boolean>(false);
  const [showNC4Document, setShowNC4Document] = useState<boolean>(false);
  const [currentI9Document, setCurrentI9Document] = useState<any>();
  const [showI9Document, setShowI9Document] = useState<boolean>(false);
  const [currentI9SpanishDocument, setCurrentI9SpanishDocument] = useState<any>();
  const [showI9SpanishDocument, setShowI9SpanishDocument] = useState<boolean>(false);
  const [BankingModalEditMode, setBankingModalEditMode] = useState<boolean>(false);
  const [applicationData, setApplicationData] = useState<any>({});
  const [showPreEmployment, setShowPreEmployment] = useState<boolean>(false);
  const [uploadingDocumentType, setUploadingDocumentType] = useState<string | null>(null);
  const [showUploadDocuments, setShowUploadDocuments] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentFile, setDocumentFile] = useState<any>();
  const [loadingUploadDocuments, setLoadingUploadDocuments] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [allHiringAgreements, setAllHiringAgreements] = useState<any[]>([]);
  const [documentsData, setDocumentsData] = useState<any[]>();
  const [initialLoading, setInitialLoading] = useState(true);

  const getDocuments = async () => {
    if (!currentUser?.id) return;
    try {
      const result = await HireDocuments(token).show(currentUser.id);
      const docs = result?.data || [];
      setDocumentsData(docs);
      buildUnifiedAgreementsList(docs);
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid user ID');
    }
  };

  const refreshAll = async () => {
    await Promise.all([getDocuments(), getEmploymentDocuments(), getBankAccounts(), getHireApplication()]);
  };

  useEffect(() => {
    const fetchAndBuildLists = async () => {
      if (!currentUser?.id) {
        setInitialLoading(false);
        return;
      }

      setInitialLoading(true);
      try {
        const [signedDocsResponse, agreementsResponse] = await Promise.all([
          HireDocuments(token).show(currentUser.id),
          HireAgreementsService(token).list(),
        ]);
        const signedDocuments = signedDocsResponse?.data || [];
        const allAgreements = agreementsResponse.data || [];

        setDocumentsData(signedDocuments);

        const unifiedList = allAgreements.map((agreement: any) => {
          const signedVersion = signedDocuments.find((doc: any) => doc.type === agreement.key);
          return {
            ...agreement,
            signedData: signedVersion,
          };
        });
        setAllHiringAgreements(unifiedList);

        await Promise.all([getEmploymentDocuments(), getBankAccounts(), getHireApplication()]);
      } catch (error) {
        toast.error('Failed to load document lists.');
        console.error(error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAndBuildLists();
  }, [currentUser, token]);

  useEffect(() => {
    if (onDocumentsUpdated) {
      onDocumentsUpdated(refreshAll);
    }
  }, [onDocumentsUpdated]);

  const buildUnifiedAgreementsList = async (signedDocuments: any[]) => {
    try {
      const agreementsResponse = await HireAgreementsService(token).list();
      const allAgreements = agreementsResponse.data || [];

      const unifiedList = allAgreements.map((agreement: any) => {
        const signedVersion = signedDocuments?.find((doc) => doc.type === agreement.key);
        return {
          ...agreement,
          signedData: signedVersion,
        };
      });

      setAllHiringAgreements(unifiedList);
    } catch (error) {
      toast.error('Failed to load hiring agreements.');
    }
  };

  const getBankAccounts = async () => {
    try {
      const result = await BankingAccountsService(token).list(currentUser?.employee?.id);
      const data: any[] = result?.data || [];
      const newBanks = data.map((u) => ({
        id: u?.id,
        bankName: u?.bankName,
        routingNumber: u?.routingNumber,
        accountNumber: u?.accountNumber,
        accountType: u?.accountType,
        url: u?.blankCheck,
        date: moment(u?.createdAt || '').format('YYYY-MM-DD'),
        employeeId: u.employeeId,
      }));
      setBanks(newBanks);
      setBankingModalEditMode(false);
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid employee ID');
    }
  };

  const getEmploymentDocuments = async () => {
    try {
      const result = await EmploymentDocumentsService(token).list(currentUser?.employee?.id);
      const data: any[] = result?.data || [];
      setUploads(
        data.map((u) => ({
          id: u?.id,
          title: u?.documentType,
          url: u?.document,
          expirationDate: u?.expirationDate,
          noExpiration: !!u?.noExpiration,
          verifiedAt: u?.verifiedAt,
        })),
      );
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid employee ID');
    }
  };

  const getHireApplication = async () => {
    try {
      const result = await HireApplication(token).show(currentUser?.id, { includeDrafts: true });
      setApplicationData(result?.data || {});
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  const handleW4Document = () => {
    const doc = documentsData?.find((d) => d?.type === 'W4');
    setCurrentW4Document(doc);
    setShowW4Document(true);
  };

  const handleIS54Document = () => {
    const doc = documentsData?.find((d) => d?.type === 'IS54');
    setCurrentIS54Document(doc);
    setShowIS54Document(true);
  };

  const handleIT2104Document = () => {
    const doc = documentsData?.find((d) => d?.type === 'IT2104');
    setCurrentIT2104Document(doc);
    setShowIT2104Document(true);
  };

  const handleNC4Document = () => {
    const doc = documentsData?.find((d) => d?.type === 'NC4');
    setCurrentNC4Document(doc);
    setShowNC4Document(true);
  };

  const handleI9Document = () => {
    const doc = documentsData?.find((d) => d?.type === 'I9');
    setCurrentI9Document(doc);
    setShowI9Document(true);
  };

  const handleI9SpanishDocument = () => {
    const doc = documentsData?.find((d) => d?.type === 'I9-Spanish');
    setCurrentI9SpanishDocument(doc);
    setShowI9SpanishDocument(true);
  };

  const onRemoveFile = (event: any, id: string) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    confirmAlert({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to delete this document?',
      onClickYes: () => deleteEmploymentDocuments(id),
    });
  };

  const onRemoveDocument = (event: any, id: string) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    confirmAlert({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to delete this document?',
      onClickYes: () => deleteHireDocument(id),
    });
  };

  const onRemoveBankAccount = (event: any, id: string) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    confirmAlert({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to delete this banking account?',
      onClickYes: () => deleteBankingAccount(id),
    });
  };

  const onRemoveApplication = (event: any, id: string) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    confirmAlert({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to delete this application form?',
      onClickYes: () => deleteApplication(id),
    });
  };

  const deleteHireDocument = async (id: string) => {
    try {
      await HireDocuments(token).delete(id);
      await refreshAll();
      toast.success(`Deleted successfully!`);
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  const deleteEmploymentDocuments = async (id: string) => {
    try {
      await EmploymentDocumentsService(token).delete(id);
      getEmploymentDocuments();
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  const deleteBankingAccount = async (id: string) => {
    try {
      await BankingAccountsService(token).delete(id);
      getBankAccounts();
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  const deleteApplication = async (id: string) => {
    try {
      await HireApplication(token).delete(id);
      getHireApplication();
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && uploadingDocumentType) {
      const currentType = uploadingDocumentType;
      setUploadingDocumentType(null);
      setIsUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('document', file as any);
        if (currentType === 'APPLICATION_PDF') {
          await HireApplication(token).uploadFile(currentUser?.id, formData);
        } else {
          await HireDocuments(token).uploadFile(currentUser?.id, currentType, formData);
        }
        toast.success(`Saved successfully!`);
        getDocuments();
        getHireApplication();
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload document.');
      } finally {
        setIsUploadingFile(false);
        if (event.target) event.target.value = '';
      }
    }
  };

  const onSaveFile = (event: any) => {
    if (event?.stopPropagation) event.stopPropagation();
    if (event?.preventDefault) event.preventDefault();
    handleSubmitDocument();
  };

  const handleSubmitDocument = async () => {
    setLoadingUploadDocuments(true);
    try {
      if (documentFile) {
        const formData = new FormData();
        formData.append('employeeId', currentUser?.employee?.id);
        formData.append('documentType', documentType);
        formData.append('document', documentFile as any);
        await EmploymentDocumentsService(token).createWithFile(formData);
      } else {
        await EmploymentDocumentsService(token).create({
          employeeId: currentUser?.employee?.id,
          documentType,
        });
      }
      toast.success(`Saved successfully!`);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    } finally {
      setDocumentType('');
      setShowUploadDocuments(false);
      getEmploymentDocuments();
      getBankAccounts();
      getHireApplication();
      setLoadingUploadDocuments(false);
    }
  };

  const renderExpiration = (upload: any) => {
    if (!upload?.verifiedAt) {
      return <span className="badge badge-warning">No expiration info</span>;
    }
    if (upload?.noExpiration || !upload?.expirationDate) {
      return <span className="text-muted">Does not expire</span>;
    }
    const expiration = moment(String(upload.expirationDate).slice(0, 10), 'YYYY-MM-DD');
    const expired = expiration.isBefore(moment(), 'day');
    return (
      <span className={expired ? 'text-danger font-weight-bold' : ''}>
        {expiration.format('MM/DD/YYYY')}
        {expired ? ' (expired)' : ''}
      </span>
    );
  };

  const handleVerifyDocument = async (answers: { id: string; expirationDate?: string; noExpiration?: boolean }[]) => {
    const [answer] = answers;
    try {
      await EmploymentDocumentsService(token).verify(answer.id, {
        expirationDate: answer.expirationDate,
        noExpiration: answer.noExpiration,
      });
      toast.success('Document updated successfully!');
      setVerifyingDocument(null);
      await getEmploymentDocuments();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not update the document');
    }
  };

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Card className="shadow border-0 mt-4">
      <CardBody className="pb-4">
        <Row>
          <Col lg="12">
            <div className="d-flex justify-content-between align-items-center py-1">
              <h3 className="text-uppercase text-primary">HIRING DOCUMENTS</h3>
            </div>
          </Col>
        </Row>
        <OfferLetterView
          loggedUser={loggedUser}
          user={currentUser}
          employee={currentUser?.employee || {}}
          setUploadingDocumentType={setUploadingDocumentType}
          fileInputRef={fileInputRef}
          uploadedOfferLetter={documentsData?.find((d) => d?.type === 'OFFER_LETTER')}
          removeAlert={removeAlert}
        />
        <Row>
          <Table className="mt-4" responsive>
            <thead>
              <tr>
                <th colSpan={4} style={{ background: '#f3f3f3', borderTop: '1px solid #dadada' }}>
                  <div className="d-flex align-items-center py-1">I-9</div>
                </th>
              </tr>
              {documentsData?.find((d) => d?.type === 'I9' || d?.type === 'I9-Spanish') && (
                <tr>
                  <th>Name</th>
                  <th>Added Date</th>
                  <th>Action</th>
                </tr>
              )}
            </thead>
            <tbody>
              {!documentsData?.find((d) => d?.type === 'I9' || d?.type === 'I9-Spanish') &&
                (isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                  <>
                    <tr>
                      <th>Name</th>
                      <th>Added Date</th>
                      <th>Action</th>
                    </tr>
                    <tr>
                      <td>I-9</td>
                      <td>-</td>
                      <td>
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            setUploadingDocumentType('I9');
                            fileInputRef.current?.click();
                          }}
                          disabled={isUploadingFile}
                        >
                          {isUploadingFile ? (
                            <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                          ) : (
                            <i className="fa fa-upload" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  </>
                )}
              {documentsData
                ?.filter((d) => d?.type === 'I9' || d?.type === 'I9-Spanish')
                .map((d: any) => {
                  return (
                    <tr key={`document-${d?.type}`} className={d?.approvedAt ? 'bg-success text-white' : ''}>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-file mr-2" />
                          {d.type}
                        </div>
                      </td>
                      <td>{moment(d?.options?.signatureDate).format('MM/DD/YYYY')}</td>
                      <td>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <Button
                            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                            className={d?.approvedAt ? 'bg-success text-white' : ''}
                            onClick={(e: any) => {
                              e?.preventDefault();
                              if (d.type === 'I9-Spanish') handleI9SpanishDocument();
                              if (d?.type === 'I9') handleI9Document();
                            }}
                          >
                            <i className="fas fa-eye" />
                          </Button>
                          {!d?.approvedAt &&
                          (isAdmin(loggedUser) ||
                            isHRUser(loggedUser) ||
                            currentUser?.employee?.supervisorId === loggedUser?.id) ? (
                            <>
                              <Button
                                style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                                onClick={async (e) => {
                                  await approveAlert(e, d?.id || '');
                                  await getDocuments();
                                }}
                              >
                                <i className="fas fa-check text-success" />
                              </Button>
                              <Button
                                style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                                onClick={async (e) => {
                                  await denyAlert(e, d?.id || '');
                                  await getDocuments();
                                }}
                              >
                                <i className="fas fa-ban text-danger" />
                              </Button>
                            </>
                          ) : (
                            <></>
                          )}
                          {d?.approvedAt && (isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                            <i
                              className="far fa-trash-alt text-danger"
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => onRemoveDocument(e, d.id)}
                            ></i>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </Row>
        <Row>
          <Table className="align-items-center table-flush mt-2" responsive>
            <tbody>
              {uploads?.length ? (
                <>
                  <tr>
                    <th>Title</th>
                    <th>Expiration</th>
                    <th>Action</th>
                  </tr>
                  {uploads.map((s: any) => (
                    <tr key={`uploads-${s.title}`}>
                      <td>
                        <i className="fas fa-file mr-2" />
                        <a
                          href={`${s.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline', color: '#5e72e4', backgroundColor: 'transparent' }}
                        >
                          {s.title}
                        </a>
                      </td>
                      <td>{renderExpiration(s)}</td>
                      <td>
                        {s.title && (
                          <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <a href={`${s.url}`} target="_blank" rel="noopener noreferrer">
                              <Button style={{ border: 'none', boxShadow: 'none', padding: 0 }}>
                                <i className="fas fa-eye" style={{ cursor: 'pointer' }}></i>
                              </Button>
                            </a>
                            {canVerifyDocuments && (
                              <Button
                                style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                                title={s.verifiedAt ? 'Edit expiration' : 'Verify and set expiration'}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.preventDefault();
                                  setVerifyingDocument(s);
                                }}
                              >
                                <i
                                  className={`far ${
                                    s.verifiedAt ? 'fa-calendar-alt' : 'fa-calendar-check text-warning'
                                  }`}
                                />
                              </Button>
                            )}
                            {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                              <Button style={{ border: 'none', boxShadow: 'none', padding: 0, marginLeft: 10 }}>
                                <i
                                  className="far fa-trash-alt text-danger"
                                  style={{ cursor: 'pointer' }}
                                  onClick={(e) => onRemoveFile(e, s.id)}
                                ></i>
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  <tr>
                    <td>
                      <div className="d-flex align-items-center">No Document</div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
          {!!documentsData?.filter((d) => d?.type === 'I9' || d?.type === 'I9-Spanish')?.length && (
            <UploadDocumentsForm
              loggedUser={loggedUser}
              showForm={showUploadDocuments}
              setShowForm={setShowUploadDocuments}
              setDocumentType={setDocumentType}
              setDocumentFile={setDocumentFile}
              onSaveFile={onSaveFile}
              loadingUploadDocuments={loadingUploadDocuments}
            />
          )}
        </Row>
        <Row>
          <Table className="mt-4" responsive>
            <thead>
              <tr>
                <th style={{ background: '#f3f3f3', borderTop: '1px solid #dadada' }}>
                  <div className="d-flex align-items-center py-2">Application</div>
                </th>
              </tr>
            </thead>
            <tbody></tbody>
          </Table>
          <Table className="align-items-center table-flush" responsive>
            <tbody>
              <tr>
                <th>Name</th>
                <th>Action</th>
              </tr>
              <tr>
                <td>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-file mr-2" />
                    Application Form
                    {applicationData?.id && applicationData?.isDraft && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '2px 8px',
                          background: '#FEF3C7',
                          color: '#92400E',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        Draft
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {applicationData?.id ? (
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={() => setShowPreEmployment(true)}
                      >
                        <i className="fas fa-eye" style={{ cursor: 'pointer' }}></i>
                      </Button>
                      {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                        <Button style={{ border: 'none', boxShadow: 'none', padding: 0, marginLeft: 10 }}>
                          <i
                            className="far fa-trash-alt text-danger"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onRemoveApplication(e, applicationData.id)}
                          ></i>
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      {isAdmin(loggedUser) || isHRUser(loggedUser) ? (
                        <>
                          <Button
                            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.preventDefault();
                              setUploadingDocumentType('APPLICATION_PDF');
                              fileInputRef.current?.click();
                            }}
                            disabled={isUploadingFile}
                          >
                            {isUploadingFile ? (
                              <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                            ) : (
                              <i className="fa fa-upload" />
                            )}
                          </Button>
                        </>
                      ) : (
                        <>
                          Incomplete <i className="fas fa-ban" style={{ color: 'red' }} />
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </Row>
        {isAuthorized({ moduleName: 'Bank', functionName: 'View' }, loggedUser) ||
        isAdmin(loggedUser) ||
        currentUser?.id === loggedUser?.id ? (
          <Row>
            <Table className="mt-4" responsive>
              <thead>
                <tr>
                  <th colSpan={4} style={{ background: '#f3f3f3', borderTop: '1px solid #dadada' }}>
                    <div className="d-flex align-items-center py-1">Banking Account</div>
                  </th>
                </tr>
                <tr>
                  <th>Bank Name</th>
                  <th>Registration Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {banks?.length ? (
                  <>
                    {banks.map((s: any, i: number) => (
                      <tr key={`bank-${i}`}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="fas fa-file mr-2" />
                            <a
                              href={`${s.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: 'underline',
                                color: '#5e72e4',
                                backgroundColor: 'transparent',
                              }}
                            >
                              {s.bankName}
                            </a>
                          </div>
                        </td>
                        <td>{moment(s.date).format('MM/DD/YYYY')}</td>
                        <td>
                          <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <Button
                              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                              onClick={() => {
                                setCurrentBankingAccount(s?.id || '');
                                setBankingModalEditMode(false);
                                setShowBankingModal(true);
                              }}
                            >
                              <i className="fas fa-eye" />
                            </Button>
                            <Button
                              style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                              onClick={() => {
                                setCurrentBankingAccount(s?.id || '');
                                setBankingModalEditMode(true);
                                setShowBankingModal(true);
                              }}
                            >
                              <i className="fas fa-pen text-primary" />
                            </Button>
                            {(banks?.length > 1 || isAdmin(loggedUser)) && (
                              <Button
                                style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                                onClick={(e) => onRemoveBankAccount(e, s.id)}
                              >
                                <i className="far fa-trash-alt text-danger" style={{ cursor: 'pointer' }} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : (
                  <>
                    <tr>
                      <td colSpan={3}>
                        <div>
                          <p>No Data</p>
                        </div>
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td colSpan={3}>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setCurrentBankingAccount('');
                          setBankingModalEditMode(true);
                          setShowBankingModal(true);
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Row>
        ) : (
          <></>
        )}
        {(() => {
          const hiringMain = allHiringAgreements.filter((a) => a.category !== 'MANAGEMENT');
          const hiringManagement = allHiringAgreements.filter((a) => a.category === 'MANAGEMENT');
          const renderRow = (agreement: any, i: number) => {
            const d = agreement.signedData;
            return (
              <tr key={`agreement-${agreement.id || i}`}>
                <td>
                  <div className="d-flex align-items-center">
                    <a
                      href={agreement.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', color: '#5e72e4', backgroundColor: 'transparent' }}
                    >
                      <i className="fas fa-file mr-2" />
                      {agreement.name}
                    </a>
                  </div>
                </td>
                <td>
                  {d ? (
                    <>
                      {moment(d.options?.signatureDate || d.updatedAt).format('MM/DD/YYYY')}{' '}
                      <i className="fas fa-check-circle" style={{ color: '#02ce0d' }} />
                    </>
                  ) : (
                    <>
                      Incomplete <i className="fas fa-ban" style={{ color: 'red' }} />
                    </>
                  )}
                </td>
                <td>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    {d ? (
                      <>
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e: any) => {
                            e.preventDefault();
                            handleDocument(d);
                          }}
                        >
                          <i className="fas fa-eye" />
                        </Button>
                        {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                          <Button
                            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                            onClick={(e) => onRemoveDocument(e, d.id)}
                          >
                            <i className="far fa-trash-alt text-danger" />
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                          <Button
                            style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.preventDefault();
                              setUploadingDocumentType(agreement.key);
                              fileInputRef.current?.click();
                            }}
                            disabled={isUploadingFile}
                          >
                            {isUploadingFile ? (
                              <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                            ) : (
                              <i className="fa fa-upload" />
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          };
          const renderTable = (title: string, items: any[]) => (
            <Row>
              <Table className="mt-4" responsive>
                <thead>
                  <tr>
                    <th colSpan={4} style={{ background: '#f3f3f3', borderTop: '1px solid #dadada' }}>
                      <div className="d-flex align-items-center py-1">{title}</div>
                    </th>
                  </tr>
                  <tr>
                    <th>Name</th>
                    <th>Added Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>{items.map((agreement, i) => renderRow(agreement, i))}</tbody>
              </Table>
            </Row>
          );
          return (
            <>
              {renderTable('HIRING AGREEMENTS', hiringMain)}
              {hiringManagement.length > 0 && renderTable('MANAGEMENT / OTHERS', hiringManagement)}
            </>
          );
        })()}
        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept=".pdf" />
        <Row>
          <Table className="mt-4" responsive>
            <thead>
              <tr>
                <th colSpan={4} style={{ background: '#f3f3f3', borderTop: '1px solid #dadada' }}>
                  <div className="d-flex align-items-center py-1">Government Documents</div>
                </th>
              </tr>
              <tr>
                <th>Name</th>
                <th>Added Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {documentsData?.find((d) => d?.type === 'W4') ? (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      W4 Federal Form
                    </div>
                  </td>
                  <td>
                    {moment(documentsData?.find((d) => d?.type === 'W4')?.options?.signatureDate).format('MM/DD/YYYY')}
                  </td>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          handleW4Document();
                        }}
                      >
                        <i className="fas fa-eye" />
                      </Button>
                      {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e) => removeAlert(e, documentsData?.find((d) => d?.type === 'W4')?.id || '')}
                        >
                          <i className="far fa-trash-alt text-danger" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      W4 Federal Form
                    </div>
                  </td>
                  <td>-</td>
                  <td>
                    {isAdmin(loggedUser) || isHRUser(loggedUser) ? (
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          setUploadingDocumentType('W4');
                          fileInputRef.current?.click();
                        }}
                        disabled={isUploadingFile}
                      >
                        {isUploadingFile ? (
                          <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                        ) : (
                          <i className="fa fa-upload" />
                        )}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              )}
              {documentsData?.find((d) => d?.type === 'IS54') ? (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      Notice and Acknowledgement of Pay Rate and Payday
                    </div>
                  </td>
                  <td>
                    {moment(documentsData?.find((d) => d?.type === 'IS54')?.options?.signatureDate).format(
                      'MM/DD/YYYY',
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          handleIS54Document();
                        }}
                      >
                        <i className="fas fa-eye" />
                      </Button>
                      {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e) => removeAlert(e, documentsData?.find((d) => d?.type === 'IS54')?.id || '')}
                        >
                          <i className="far fa-trash-alt text-danger" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      Notice and Acknowledgement of Pay Rate and Payday
                    </div>
                  </td>
                  <td>-</td>
                  <td>
                    {isAdmin(loggedUser) || isHRUser(loggedUser) ? (
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          setUploadingDocumentType('IS54');
                          fileInputRef.current?.click();
                        }}
                        disabled={isUploadingFile}
                      >
                        {isUploadingFile ? (
                          <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                        ) : (
                          <i className="fa fa-upload" />
                        )}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              )}
              {documentsData?.find((d) => d?.type === 'IT2104') ? (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      NY Employee Withholding Allowance
                    </div>
                  </td>
                  <td>
                    {moment(documentsData?.find((d) => d?.type === 'IT2104')?.options?.signatureDate).format(
                      'MM/DD/YYYY',
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          handleIT2104Document();
                        }}
                      >
                        <i className="fas fa-eye" />
                      </Button>
                      {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e) => removeAlert(e, documentsData?.find((d) => d?.type === 'IT2104')?.id || '')}
                        >
                          <i className="far fa-trash-alt text-danger" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      NY Employee Withholding Allowance
                    </div>
                  </td>
                  <td>-</td>
                  <td>
                    {isAdmin(loggedUser) || isHRUser(loggedUser) ? (
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          setUploadingDocumentType('IT2104');
                          fileInputRef.current?.click();
                        }}
                        disabled={isUploadingFile}
                      >
                        {isUploadingFile ? (
                          <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                        ) : (
                          <i className="fa fa-upload" />
                        )}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              )}
              {documentsData?.find((d) => d?.type === 'NC4') ? (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      NC Withholding Allowance
                    </div>
                  </td>
                  <td>
                    {moment(documentsData?.find((d) => d?.type === 'NC4')?.options?.signatureDate).format('MM/DD/YYYY')}
                  </td>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          handleNC4Document();
                        }}
                      >
                        <i className="fas fa-eye" />
                      </Button>
                      {(isAdmin(loggedUser) || isHRUser(loggedUser)) && (
                        <Button
                          style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                          onClick={(e) => removeAlert(e, documentsData?.find((d) => d?.type === 'NC4')?.id || '')}
                        >
                          <i className="far fa-trash-alt text-danger" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file mr-2" />
                      NC Withholding Allowance
                    </div>
                  </td>
                  <td>-</td>
                  <td>
                    {isAdmin(loggedUser) || isHRUser(loggedUser) ? (
                      <Button
                        style={{ border: 'none', boxShadow: 'none', padding: 0 }}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          setUploadingDocumentType('NC4');
                          fileInputRef.current?.click();
                        }}
                        disabled={isUploadingFile}
                      >
                        {isUploadingFile ? (
                          <Spinner size="sm" style={{ width: '14px', height: '14px' }} />
                        ) : (
                          <i className="fa fa-upload" />
                        )}
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Row>
      </CardBody>
      <BankingAccountModal
        showBankingModal={showBankingModal}
        bankAccountId={currentBankingAccount}
        banks={banks}
        setShowBankingModal={setShowBankingModal}
        editMode={BankingModalEditMode}
        onSave={(_) => {
          getBankAccounts();
          setShowBankingModal(!showBankingModal);
        }}
        employeeId={currentUser?.employee?.id}
      />
      <W4DocumentViewModal
        data={currentW4Document}
        isOpen={showW4Document}
        toggleModal={() => setShowW4Document(false)}
      />
      <I9DocumentViewModal
        data={currentI9Document}
        isOpen={showI9Document}
        toggleModal={() => setShowI9Document(false)}
      />
      <I9SpanishDocumentViewModal
        data={currentI9SpanishDocument}
        isOpen={showI9SpanishDocument}
        toggleModal={() => setShowI9SpanishDocument(false)}
      />
      <PreEmploymentFormModal
        data={applicationData.json}
        uploadedPdf={applicationData.uploadedPdf}
        isOpen={showPreEmployment}
        toggleModal={() => setShowPreEmployment(false)}
      />
      <IS54FormModal
        data={{ ...currentIS54Document?.options, employeeSignature: currentIS54Document?.signature }}
        uploadedPdf={currentIS54Document?.uploadedPdf}
        isOpen={showIS54Document}
        toggleModal={() => setShowIS54Document(false)}
      />
      <IT2104PreviewModal
        formData={{ ...currentIT2104Document?.options, employeeSignature: currentIT2104Document?.signature }}
        uploadedPdf={currentIT2104Document?.uploadedPdf}
        isOpen={showIT2104Document}
        toggleModal={() => setShowIT2104Document(false)}
      />
      <NC4PreviewModal
        formData={{ ...currentNC4Document?.options, employeeSignature: currentNC4Document?.signature }}
        uploadedPdf={currentNC4Document?.uploadedPdf}
        isOpen={showNC4Document}
        toggleModal={() => setShowNC4Document(false)}
      />
      <VerifyEmploymentDocumentsModal
        isOpen={!!verifyingDocument}
        title="Document expiration"
        description="Enter the expiration date printed on the document, or mark it as not expiring."
        confirmLabel="Save"
        documents={verifyingDocument ? [verifyingDocument] : []}
        toggleModal={() => setVerifyingDocument(null)}
        onConfirm={handleVerifyDocument}
      />
    </Card>
  );
};

export default NewHireDocumentsView;
